import React from "react";
import { Link, useParams } from "react-router-dom";
import { Landmark, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { GOV_TOPICS, GOV_TOPIC_GUIDE, breadcrumbLd } from "@/lib/siteContent";
import { Button } from "@/components/ui/button";

const REVIEWED = "May 2026";

export default function GovernmentHelp() {
  const { topic } = useParams();
  const current = GOV_TOPICS.find((t) => t[0] === topic);

  if (topic && current) {
    const [slug, name, blurb] = current;
    const crumbs = [{ label: "Home", to: "/" }, { label: "Government Help", to: "/government-help" }, { label: name, to: `/government-help/${slug}` }];
    return (
      <div className="ca-container py-12 max-w-3xl">
        <Seo title={`${name} in India | Official Complaint Guidance | ComplaintAdvisor`}
          description={`${blurb} Learn when to use the channel, what to prepare, and how to file a complaint through official portals.`}
          path={`/government-help/${slug}`} jsonld={breadcrumbLd(crumbs)} />
        <Breadcrumbs items={crumbs} />
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-3">{name}</h1>
        <p className="text-slate-700 mt-2">{blurb}</p>

        <h2 className="font-display text-lg font-bold text-slate-900 mt-8">When to use this channel</h2>
        <p className="text-slate-600 mt-2">{GOV_TOPIC_GUIDE.whenToUse}</p>

        <h2 className="font-display text-lg font-bold text-slate-900 mt-8">What information to prepare</h2>
        <ul className="mt-2 list-disc pl-5 space-y-1 text-slate-600">{GOV_TOPIC_GUIDE.prepare.map((p) => <li key={p}>{p}</li>)}</ul>

        <h2 className="font-display text-lg font-bold text-slate-900 mt-8">Step-by-step filing guidance</h2>
        <ol className="mt-2 list-decimal pl-5 space-y-1 text-slate-600">{GOV_TOPIC_GUIDE.steps.map((s) => <li key={s}>{s}</li>)}</ol>

        <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-sm text-slate-700 font-medium">Official portal</p>
          <p className="text-xs text-slate-500 mt-1">
            We do not publish unverified helpline numbers or portal links. Find the official destination via the{" "}
            <Link to="/government-guidelines" className="text-teal">verified government resources list</Link> or the National Consumer Helpline.
          </p>
        </div>

        <p className="text-xs text-slate-400 mt-6">Last reviewed: {REVIEWED}. Official details may change — always confirm on the government's official website. This is general information, not legal advice.</p>
        <div className="mt-4"><Link to="/file-a-complaint"><Button variant="outline">File a structured complaint</Button></Link></div>
      </div>
    );
  }

  const crumbs = [{ label: "Home", to: "/" }, { label: "Government Help", to: "/government-help" }];
  return (
    <div className="ca-container py-12">
      <Seo title="Government Complaint Help in India | Official Complaint Portals"
        description="Find guidance and official complaint channels for consumer, banking, insurance, cybercrime, telecom, railway, airline, food safety, property, and public-service issues in India."
        path="/government-help" jsonld={breadcrumbLd(crumbs)} />
      <Breadcrumbs items={crumbs} />
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-3 flex items-center gap-2"><Landmark className="h-7 w-7 text-teal" /> Government Complaint Help</h1>
      <p className="text-slate-700 mt-2 max-w-2xl">
        ComplaintAdvisor provides informational guidance and links to official complaint channels. The correct authority
        depends on the subject, location, service provider, transaction type, and applicable rules.
      </p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {GOV_TOPICS.map(([slug, name, blurb]) => (
          <Link key={slug} to={`/government-help/${slug}`} data-testid={`gov-topic-${slug}`}
            className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-teal-brand/40 hover:shadow-sm transition-all">
            <p className="font-semibold text-slate-900">{name}</p>
            <p className="text-xs text-slate-500 mt-1">{blurb}</p>
            <p className="text-[11px] text-teal mt-2 inline-flex items-center gap-1">Read guidance <ExternalLink className="h-3 w-3" /></p>
          </Link>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-8">This content is for general information only and is not legal advice. Rules, procedures, jurisdiction, and deadlines may vary.</p>
    </div>
  );
}
